import { CALC_SOURCES, COLUMN_CATALOG_MAP, ORION_ENTITY_GROUPINGS, getBenchmarkCounterpartSource } from './constants'
import { DERIVE_REGISTRY, getMergedDependencyOpts, resolveCalcInputs } from './derivedColumns'
import { resolveColumnOptions } from './calcResolve'
import { compileScopeFilter } from './scopeFilterCompile'

export { resolveColumnOptions } from './calcResolve'

// Sentinel id for the comparison sub-grouping. Shared contract between
// request emission (this module) and response demux (`partitionCalculations`
// in transformResponse.js). Renaming requires both sites; collisions with
// any other calc.id will misroute the response. Keep this string unique to
// the benchmarks fan-out feature.
export const COMPARISON_GROUP_ID = 'benchmarks'

function isDerivedColumn (columnType) {
  return COLUMN_CATALOG_MAP[columnType]?.kind === 'derived'
}

function isWindowColumn (columnType) {
  return COLUMN_CATALOG_MAP[columnType]?.kind === 'window'
}

function isComparisonColumn (columnType) {
  return COLUMN_CATALOG_MAP[columnType]?.kind === 'comparison'
}

function isClientOnlyColumn (columnType) {
  return isDerivedColumn(columnType) || isWindowColumn(columnType) || isComparisonColumn(columnType)
}

function syntheticIdForCalcKey (calcKey) {
  return `__req_${calcKey.replace(/[^a-zA-Z0-9]+/g, '_')}`
}

/**
 * Appends synthetic material column instances so every distinct calcKey required by
 * derived formulas is present in resolveCalcAssignments (Orion dedupes by key).
 */
export function expandColumnsWithDerivedDeps (columns, globalDateConfig) {
  const material = columns.filter(c => !isClientOnlyColumn(c.columnType))
  const materialAssignments = resolveCalcAssignments(material, globalDateConfig)
  const coveredKeys = new Set(materialAssignments.keys())
  const synthetics = []

  for (const col of columns) {
    const catalog = COLUMN_CATALOG_MAP[col.columnType]
    if (!catalog || catalog.kind !== 'derived') continue
    const def = DERIVE_REGISTRY[catalog.deriveId]
    if (!def) continue
    const inputs = resolveCalcInputs(def, col)
    if (!inputs.length) continue

    for (const input of inputs) {
      const source = CALC_SOURCES[input.source]
      if (!source) continue
      const mergedOpts = getMergedDependencyOpts(input, col.options)
      if (!mergedOpts) continue
      const resolved = resolveColumnOptions(mergedOpts, globalDateConfig)
      // Honor shouldSkip the same way resolveCalcAssignments does, so
      // synthetics whose options would be rejected at emission time are
      // also not registered against coveredKeys.
      if (typeof source.shouldSkip === 'function' && source.shouldSkip(resolved)) continue
      const key = source.calcKey(resolved)
      if (coveredKeys.has(key)) continue
      coveredKeys.add(key)
      synthetics.push({
        columnType: input.representativeColumnType,
        id: syntheticIdForCalcKey(key),
        options: mergedOpts
      })
    }
  }

  return synthetics.length > 0 ? [...columns, ...synthetics] : columns
}

/**
 * Groups columns by calcKey, deduplicating so that columns sharing the same
 * calc source + resolved options produce a single Orion calculation.
 * Returns { calcs: OrionCalcObject[], calcAssignments: Map<calcKey, { calcId, source, resolvedOptions, columnIds[] }> }
 */
export function resolveCalcAssignments (columns, globalDateConfig) {
  const groups = new Map()
  let calcIdCounter = 0

  for (const col of columns) {
    const catalog = COLUMN_CATALOG_MAP[col.columnType]
    if (!catalog) continue
    if (isClientOnlyColumn(col.columnType)) continue
    const source = CALC_SOURCES[catalog.calcSource]
    if (!source) continue

    const resolved = resolveColumnOptions(col.options, globalDateConfig)

    // Sources can opt a column out of Orion emission when its options are
    // incomplete (e.g. activityType when no activity types are selected).
    // The column's row value stays undefined and renders as `--`.
    if (typeof source.shouldSkip === 'function' && source.shouldSkip(resolved)) continue

    const key = source.calcKey(resolved)

    if (!groups.has(key)) {
      const calcId = `calc_${calcIdCounter++}`
      groups.set(key, {
        calcId,
        sourceName: catalog.calcSource,
        source,
        resolvedOptions: resolved,
        columnIds: []
      })
    }
    groups.get(key).columnIds.push(col.id)
  }

  return groups
}

/**
 * Derives benchmark calc assignments from the host (material) calc assignments.
 * Each host group with a `benchmarkCounterpart` produces a benchmark calc that
 * shares the host group's calcId (Orion accepts duplicate ids across sibling
 * subtrees). Multiple host groups whose benchmark counterparts resolve to the
 * same benchmark calcKey are merged into a single benchmark group whose
 * `columnIds` is the union of contributing host columnIds.
 *
 * When two host groups produce the same benchmark counterpart calcKey, only
 * the first host's calcId is retained on the merged benchmark entry; subsequent
 * host calcIds are intentionally dropped. This is safe because Orion only needs
 * one benchmark calc emitted per unique counterpart calcKey -- its single calcId
 * is in the response, and the demux maps that id to the union of contributing
 * host columnIds via `columnIds`. Multiple distinct host calcIds for the same
 * benchmark would produce duplicate Orion calcs in the Comparison subtree;
 * harmless but wasteful.
 */
export function resolveBenchmarkAssignments (calcAssignments) {
  const benchmarkGroups = new Map()

  for (const hostGroup of calcAssignments.values()) {
    const counterpartSourceName = getBenchmarkCounterpartSource(hostGroup.sourceName, hostGroup.resolvedOptions)
    if (!counterpartSourceName) continue
    const counterpartSource = CALC_SOURCES[counterpartSourceName]
    if (!counterpartSource) continue

    const benchmarkKey = counterpartSource.calcKey(hostGroup.resolvedOptions)

    if (!benchmarkGroups.has(benchmarkKey)) {
      benchmarkGroups.set(benchmarkKey, {
        calcId: hostGroup.calcId,
        sourceName: counterpartSourceName,
        source: counterpartSource,
        resolvedOptions: hostGroup.resolvedOptions,
        columnIds: []
      })
    }
    const entry = benchmarkGroups.get(benchmarkKey)
    for (const id of hostGroup.columnIds) {
      if (!entry.columnIds.includes(id)) entry.columnIds.push(id)
    }
  }

  return benchmarkGroups
}

export function buildComparisonGroup (benchmarkCalcs) {
  return {
    id: COMPARISON_GROUP_ID,
    $type: 'grouping',
    grouping: 'Comparison',
    calculations: benchmarkCalcs
  }
}

function buildCalcs (columns, globalDateConfig, includeBenchmarks = false) {
  const expanded = expandColumnsWithDerivedDeps(columns, globalDateConfig)
  const groups = resolveCalcAssignments(expanded, globalDateConfig)
  const calcs = []

  for (const group of groups.values()) {
    calcs.push(group.source.buildCalculation(group.calcId, group.resolvedOptions))
  }

  const benchmarkCalcs = []
  if (includeBenchmarks) {
    const benchmarkGroups = resolveBenchmarkAssignments(groups)
    for (const group of benchmarkGroups.values()) {
      benchmarkCalcs.push(group.source.buildCalculation(group.calcId, group.resolvedOptions))
    }
  }

  return { calcs, benchmarkCalcs }
}

/**
 * Per-grouping group-info calcs that aren't column-driven. Today this is
 * just the Account-level Nickname (SMT-1619): always requested when grouping
 * by Account so the user-preference toggle for nickname-display is instant
 * (no refetch). Other grouping levels emit no extras and are unaffected.
 */
function buildGroupingExtras (grouping) {
  if (grouping === 'Account') {
    return [{ $type: 'group-info', id: 'nickname', property: 'Nickname' }]
  }
  return []
}

// Note: an earlier version of this module attached a context-rebased
// copy of each per-grouping filter to the root-level (totalRow) leaf
// calcs to make the household-level rollup reflect the per-grouping
// filter. Empirically that path is silently ignored by Orion -- the
// `FilterInGroupingRoutine` codepath in
// OAT/Common/OAS.DataModel/Reporting/DataEngine/Contexts/ContextFilterExtension.cs
// has a `/// this doesn't work right now` comment, and a request with
// cross-context root filters returns unfiltered totals (verified
// against a live Orion response). Removed the wrap so the request shape
// stays clean. Open follow-up: the household totalRow can't reflect
// per-grouping scope filters until that server-side path is fixed; the
// Total at the bottom of the grid intentionally shows household totals.

function nestGroupings (groupings, calcs, comparisonGroup = null, scopeFilters = null) {
  return [
    groupings.reduceRight((innerCalcs, grouping, index) => {
      const extras = buildGroupingExtras(grouping)
      const tail = comparisonGroup ? [...extras, comparisonGroup] : extras
      // SMT-1608 Phase 1: per-grouping scope filter. compileScopeFilter
      // returns either a wire-format object/string (filter-collection,
      // single predicate, or "HasValue"/"HasUnits" shorthand) or null
      // when the grouping has no filter -- in which case the `filter`
      // field is omitted entirely so the request payload stays clean for
      // the no-filter case (the common case today).
      const filter = compileScopeFilter(scopeFilters?.[grouping])
      return [{
        id: `grouping_${index}_${grouping}`,
        $type: 'grouping',
        grouping,
        ...(filter !== null ? { filter } : {}),
        calculations: [...calcs, ...tail, ...innerCalcs]
      }]
    }, [])[0]
  ]
}

/**
 * Builds the Orion Reporting/Scope request body from user config.
 * Includes entity/entityIds for Orion scoping. The frontend owns entity resolution.
 */
export function buildOrionRequest ({ groupings, columns, dateMode, quickDate, startDate, endDate, includeBenchmarks, scopeFilters }, { asOfDate, maxGroupingDepth, entity, entityIds }) {
  const globalDateConfig = { dateMode, quickDate, startDate, endDate }
  const { calcs, benchmarkCalcs } = buildCalcs(columns, globalDateConfig, includeBenchmarks)
  const effectiveGroupings = maxGroupingDepth != null
    ? groupings.slice(0, maxGroupingDepth)
    : groupings

  const comparisonGroup = benchmarkCalcs.length > 0 ? buildComparisonGroup(benchmarkCalcs) : null
  const rootBenchmarkCalcs = comparisonGroup ? [comparisonGroup] : []

  const request = { entity, entityIds, asOfDate }

  if (effectiveGroupings.length === 0) {
    request.calculations = [...calcs, ...rootBenchmarkCalcs]
  } else {
    request.calculations = [...calcs, ...rootBenchmarkCalcs, ...nestGroupings(effectiveGroupings, calcs, comparisonGroup, scopeFilters)]
  }

  return request
}

/**
 * Builds a child request for lazy expand — uses remaining groupings
 * starting from the given depth index, scoped to a specific group entity.
 * Pass maxGroupingDepth to limit how many grouping levels are included.
 */
export function buildChildRequest ({ groupings, columns, dateMode, quickDate, startDate, endDate, includeBenchmarks, scopeFilters }, { asOfDate, depthIndex, maxGroupingDepth, entity, entityIds }) {
  const globalDateConfig = { dateMode, quickDate, startDate, endDate }
  const { calcs, benchmarkCalcs } = buildCalcs(columns, globalDateConfig, includeBenchmarks)
  const remainingGroupings = groupings.slice(depthIndex)
  const effectiveGroupings = maxGroupingDepth != null
    ? remainingGroupings.slice(0, maxGroupingDepth)
    : remainingGroupings

  const comparisonGroup = benchmarkCalcs.length > 0 ? buildComparisonGroup(benchmarkCalcs) : null

  const request = { entity, entityIds, asOfDate }

  if (effectiveGroupings.length === 0) {
    request.calculations = comparisonGroup ? [...calcs, comparisonGroup] : calcs
  } else {
    request.calculations = nestGroupings(effectiveGroupings, calcs, comparisonGroup, scopeFilters)
  }

  return request
}

/**
 * Builds a child request for virtual (non-entity-scopable) groupings.
 * Scopes to the nearest entity-scopable ancestor and adds a string-equal
 * filter on the virtual grouping name to narrow results server-side.
 * Pass maxGroupingDepth to limit how many inner grouping levels are included.
 */
export function buildFilteredChildRequest ({ groupings, columns, dateMode, quickDate, startDate, endDate, includeBenchmarks, scopeFilters }, {
  asOfDate,
  depthIndex,
  maxGroupingDepth,
  entity,
  entityIds,
  filterGrouping,
  filterName
}) {
  const globalDateConfig = { dateMode, quickDate, startDate, endDate }
  const { calcs, benchmarkCalcs } = buildCalcs(columns, globalDateConfig, includeBenchmarks)
  const remainingGroupings = groupings.slice(depthIndex)
  const effectiveGroupings = maxGroupingDepth != null
    ? remainingGroupings.slice(0, maxGroupingDepth)
    : remainingGroupings

  const comparisonGroup = benchmarkCalcs.length > 0 ? buildComparisonGroup(benchmarkCalcs) : null

  if (effectiveGroupings.length === 0) {
    return {
      entity,
      entityIds,
      asOfDate,
      calculations: comparisonGroup ? [...calcs, comparisonGroup] : calcs
    }
  }

  const innerGrouping = nestGroupings(effectiveGroupings, calcs, comparisonGroup, scopeFilters)[0]

  const wrappedCalc = {
    id: `filter_${filterGrouping}`,
    $type: 'grouping',
    grouping: filterGrouping,
    filters: [{
      $type: 'string-equal',
      path: ORION_ENTITY_GROUPINGS.has(filterGrouping)
        ? `group-info:${filterGrouping}:Name`
        : 'group-info',
      haystack: [filterName]
    }],
    calculations: [innerGrouping]
  }

  return { entity, entityIds, asOfDate, calculations: [wrappedCalc] }
}
